import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface IconSet {
  colorThemes: [];
  height: number;
  icons: Icon[];
  id: number;
  invisible: boolean;
  metadata: {};
  prevSize: number;
  selection: [
    {
      id: number;
      ligatures: string;
      name: string;
      order: number;
      prevSize: number;
    }
  ];
}

export interface VincleFont {
  iconSets: IconSet[];
  metaData: {};
  preferences: {};
  uid: number;
}

export interface Icon {
  attrs: [{}];
  grid: number;
  id: number;
  isMulticolor: boolean;
  isMulticolor2: boolean;
  paths: string[];
  tags: string[];
  width: number;
}

export interface IconPage {
  icons: string[];
  total: number;
  selectedPage: number;
}

@Injectable({
  providedIn: 'root'
})
export class VincleFontService {
  constructor(private http: HttpClient) {}

  /**
   * To load the icons
   * @param search the search (in the input search)
   * @param pageIndex the index of the selected page (in case of changing the page)
   * @param pageSize the number of items to display per page
   * @param selected the icon selected by default to open the page where the icon is located
   * @param pageChanged if the user has changed pages
   */
  loadIcons(
    search: string,
    pageIndex: number,
    pageSize: number,
    selected?: string,
    pageChanged?: boolean
  ): Observable<IconPage> {
    // Get from resource
    return this.http.get<VincleFont>('assets/icons/vincle_font.json').pipe(
      // Data processing to give it the format that is needed
      map(font => font.iconSets),
      map(iconsets => ({ vincle: iconsets[0].selection, material: iconsets[1].selection })),
      map(selection =>
        selection.vincle
          .map(elem => elem.name)
          .concat(selection.material.map(elem => elem.name))
          .sort((a, b) => a.localeCompare(b))
      ),
      // Filter by search term
      map(result => (search !== '' && search !== null ? result.filter(elem => elem.includes(search)) : result)),
      // We page the results
      map(result => ({
        icons: this.paginate(
          result,
          this.getSelectedPage(result, pageChanged, selected, pageSize, pageIndex, search),
          pageSize
        ),
        total: result.length,
        selectedPage: this.getSelectedPage(result, pageChanged, selected, pageSize, pageIndex, search)
      }))
    );
  }

  /**
   * Function to get the selected page
   * @param icons the list of icons
   * @param pageChanged if the user has changed pages
   * @param selected the icon selected by default to open the page where the icon is located
   * @param pageSize the number of items to display per page
   * @param pageIndex the index of the selected page (in case of changing the page)
   * @param search the search (in the input search)
   */
  protected getSelectedPage(
    icons: string[],
    pageChanged: boolean,
    selected: string,
    pageSize: number,
    pageIndex: number,
    search: string
  ) {
    return search ? 0 : !pageChanged ? (!selected ? 0 : Math.ceil(icons.indexOf(selected) / pageSize) - 1) : pageIndex;
  }

  /**
   * Function to paginate an array
   * @param array the array to paginate
   * @param pageIndex the index of the page to receive
   * @param pageSize the number of items per page to receive
   */
  protected paginate(array: any[], pageIndex: number, pageSize: number) {
    return array.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
  }
}
